import React, { useState } from 'react';
import { OrderDetailsDTO, TimelineEntry } from '../../types/order';
import StatusChip from '../shared/StatusChip';
import {
  Package,
  Calendar,
  Ruler,
  Scissors,
  Truck,
  CheckCircle,
  CheckCircle2,
  Clock,
  CalendarCheck,
  Sparkles,
  User,
  ChevronDown,
  ChevronUp,
  type LucideIcon
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

interface TimelineCardProps {
  order: OrderDetailsDTO;
  progress?: {
    percentage: number;
    completedStages: number;
    totalStages: number;
    currentStage?: string | null;
  };
}

const TimelineCard: React.FC<TimelineCardProps> = ({ order, progress }) => {
  const { user } = useAuth();
  const [expandedEntries, setExpandedEntries] = useState<Set<string>>(new Set());
  
  const toggleExpand = (entryId: string) => {
    const newExpanded = new Set(expandedEntries);
    if (newExpanded.has(entryId)) {
      newExpanded.delete(entryId);
    } else {
      newExpanded.add(entryId);
    }
    setExpandedEntries(newExpanded);
  };
  
  const formatDateTime = (dateString?: string | null): string => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  const formatDate = (dateString?: string | null): string => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  
  const hasEnhancedData = (entry: TimelineEntry): boolean => {
    return !!(
      entry.assignedWorker ||
      entry.estimatedStartAt ||
      entry.estimatedCompletionAt ||
      entry.actualStartedAt ||
      entry.actualCompletedAt ||
      entry.duration ||
      entry.notes ||
      entry.internalNotes
    );
  };
  
  // Icon mapping for timeline entries
  const getIcon = (iconName?: string): React.ReactNode => {
    const iconClass = "w-5 h-5";
    const iconMap: Record<string, LucideIcon> = {
      package: Package,
      calendar: Calendar,
      ruler: Ruler,
      scissors: Scissors,
      truck: Truck,
      'check-circle': CheckCircle,
      'check-circle-2': CheckCircle2,
      needle: Scissors, // Using scissors as needle icon
      sparkles: Sparkles,
      iron: Sparkles, // Using sparkles as iron icon
      'calendar-check': CalendarCheck
    };

    const IconComponent = iconMap[iconName || 'package'] || Package;
    return <IconComponent className={iconClass} />;
  };

  // Get status color
  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'COMPLETED':
      case 'READY':
        return 'bg-green-600 border-green-700';
      case 'IN_PROGRESS':
        return 'bg-blue-600 border-blue-700';
      case 'PENDING':
        return 'bg-gray-300 border-gray-400';
      case 'CREATED':
        return 'bg-purple-600 border-purple-700';
      default:
        return 'bg-gray-400 border-gray-500';
    }
  };

  // Get status dot color
  const getStatusDotColor = (status: string): string => {
    switch (status) {
      case 'COMPLETED':
      case 'READY':
        return 'bg-green-500';
      case 'IN_PROGRESS':
        return 'bg-blue-500 animate-pulse';
      case 'PENDING':
        return 'bg-gray-300';
      case 'CREATED':
        return 'bg-purple-500';
      default:
        return 'bg-gray-400';
    }
  };

  // Get line color
  const getLineColor = (status: string): string => {
    switch (status) {
      case 'COMPLETED':
      case 'READY':
        return 'bg-green-200';
      case 'IN_PROGRESS':
        return 'bg-blue-200';
      case 'PENDING':
        return 'bg-gray-200';
      default:
        return 'bg-gray-200';
    }
  };

  if (order.timeline.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Timeline</h3>
        <div className="text-center py-8 text-gray-500">
          <p>No timeline entries yet</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900">Order Progress Timeline</h3>
        {progress && (
          <div className="flex items-center gap-3">
            <div className="text-right">
              <div className="text-xs text-gray-500">Progress</div>
              <div className="text-sm font-semibold text-gray-900">{progress.percentage}%</div>
            </div>
            <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-blue-600 transition-all duration-300"
                style={{ width: `${progress.percentage}%` }}
              />
            </div>
          </div>
        )}
      </div>

      {progress?.currentStage && (
        <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-blue-600" />
            <span className="text-sm font-medium text-blue-900">
              Current Stage: {progress.currentStage}
            </span>
          </div>
        </div>
      )}
      
      <ol className="space-y-6" role="list" aria-label="Order timeline">
        {order.timeline.map((entry: TimelineEntry, index: number) => {
          const isPending = entry.status === 'PENDING' || !entry.at;
          const statusColor = getStatusColor(entry.status);
          const statusDotColor = getStatusDotColor(entry.status);
          const lineColor = getLineColor(entry.status);
          
          return (
            <li key={entry.id} className="relative pl-10 pb-6">
              {/* Timeline line */}
              {index < order.timeline.length - 1 && (
                <div className={`absolute left-4 top-12 bottom-0 w-0.5 ${lineColor}`}></div>
              )}
              
              {/* Timeline icon container */}
              <div className="absolute left-0 top-0">
                <div className={`w-8 h-8 rounded-full ${statusColor} border-2 border-white flex items-center justify-center shadow-sm`}>
                  <div className="text-white">
                    {getIcon(entry.icon)}
                  </div>
                </div>
                {!isPending && (
                  <div className={`absolute -bottom-1 -right-1 w-3 h-3 rounded-full ${statusDotColor} border-2 border-white`}></div>
                )}
              </div>
              
              {/* Timeline content */}
              <div className="pt-1">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-semibold text-gray-900">{entry.title}</h4>
                      {entry.stageName && (
                        <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full">
                          {entry.stageName}
                        </span>
                      )}
                    </div>
                    {entry.detail && (
                      <p className="text-sm text-gray-600 mb-2">{entry.detail}</p>
                    )}
                  </div>
                  <StatusChip status={entry.status} className="text-xs ml-2 flex-shrink-0" />
                </div>
                
                <div className="flex items-center gap-3 text-xs text-gray-500 flex-wrap">
                  {entry.at ? (
                    <>
                      <span>{formatDateTime(entry.at)}</span>
                      {entry.by && (
                        <>
                          <span>•</span>
                          <span className="flex items-center gap-1">
                            <span>by</span>
                            <span className="font-medium text-gray-700">{entry.by}</span>
                          </span>
                        </>
                      )}
                    </>
                  ) : (
                    <span className="text-gray-400 italic">Pending</span>
                  )}
                </div>
                
                {/* Enhanced Information Section */}
                {hasEnhancedData(entry) && (
                  <div className="mt-3 border-t border-gray-200 pt-3">
                    <button
                      onClick={() => toggleExpand(entry.id)}
                      className="flex items-center gap-2 text-xs text-blue-600 hover:text-blue-700 font-medium w-full justify-between"
                    >
                      <span>View Details</span>
                      {expandedEntries.has(entry.id) ? (
                        <ChevronUp className="w-4 h-4" />
                      ) : (
                        <ChevronDown className="w-4 h-4" />
                      )}
                    </button>
                    
                    {expandedEntries.has(entry.id) && (
                      <div className="mt-3 space-y-3 text-xs">
                        {/* Worker Assignment */}
                        {entry.assignedWorker && (
                          <div className="flex items-start gap-2 p-2 bg-blue-50 rounded">
                            <User className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                            <div>
                              <div className="font-medium text-gray-900">Assigned Worker</div>
                              <div className="text-gray-600">{entry.assignedWorker.name}</div>
                            </div>
                          </div>
                        )}
                        
                        {/* Estimated vs Actual Times */}
                        {(entry.estimatedStartAt || entry.estimatedCompletionAt || entry.actualStartedAt || entry.actualCompletedAt) && (
                          <div className="p-2 bg-gray-50 rounded space-y-2">
                            <div className="font-medium text-gray-900 mb-2">Timeline</div>
                            
                            {entry.estimatedStartAt && (
                              <div className="flex justify-between">
                                <span className="text-gray-600">Estimated Start:</span>
                                <span className="font-medium text-gray-900">{formatDateTime(entry.estimatedStartAt)}</span>
                              </div>
                            )}
                            
                            {entry.actualStartedAt && (
                              <div className="flex justify-between">
                                <span className="text-gray-600">Actual Start:</span>
                                <span className={`font-medium ${entry.estimatedStartAt && new Date(entry.actualStartedAt) > new Date(entry.estimatedStartAt) ? 'text-red-600' : 'text-green-600'}`}>
                                  {formatDateTime(entry.actualStartedAt)}
                                  {entry.estimatedStartAt && new Date(entry.actualStartedAt) > new Date(entry.estimatedStartAt) && (
                                    <span className="ml-1 text-xs">(Delayed)</span>
                                  )}
                                </span>
                              </div>
                            )}
                            
                            {entry.estimatedCompletionAt && (
                              <div className="flex justify-between">
                                <span className="text-gray-600">Estimated Completion:</span>
                                <span className="font-medium text-gray-900">{formatDateTime(entry.estimatedCompletionAt)}</span>
                              </div>
                            )}
                            
                            {entry.actualCompletedAt && (
                              <div className="flex justify-between">
                                <span className="text-gray-600">Actual Completion:</span>
                                <span className={`font-medium ${entry.estimatedCompletionAt && new Date(entry.actualCompletedAt) > new Date(entry.estimatedCompletionAt) ? 'text-red-600' : 'text-green-600'}`}>
                                  {formatDateTime(entry.actualCompletedAt)}
                                  {entry.estimatedCompletionAt && new Date(entry.actualCompletedAt) > new Date(entry.estimatedCompletionAt) && (
                                    <span className="ml-1 text-xs">(Delayed)</span>
                                  )}
                                </span>
                              </div>
                            )}
                          </div>
                        )}
                        
                        {/* Duration */}
                        {entry.duration && (
                          <div className="flex items-center gap-2 p-2 bg-green-50 rounded">
                            <Clock className="w-4 h-4 text-green-600 flex-shrink-0" />
                            <div>
                              <div className="font-medium text-gray-900">Duration</div>
                              <div className="text-gray-600">
                                {entry.duration.formatted}
                                {entry.duration.inProgress && (
                                  <span className="ml-1 text-blue-600">(in progress)</span>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {/* Notes */}
                        {entry.notes && (
                          <div className="p-2 bg-yellow-50 rounded border-l-2 border-yellow-400">
                            <div className="font-medium text-gray-900 mb-1">Notes</div>
                            <div className="text-gray-700 whitespace-pre-wrap">{entry.notes}</div>
                          </div>
                        )}
                        
                        {/* Internal Notes (only for tailors/admins) */}
                        {entry.internalNotes && (user?.role === 'tailor' || user?.role === 'admin') && (
                          <div className="p-2 bg-red-50 rounded border-l-2 border-red-400">
                            <div className="font-medium text-gray-900 mb-1">Internal Notes</div>
                            <div className="text-gray-700 whitespace-pre-wrap">{entry.internalNotes}</div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
};

export default TimelineCard;

